/* eslint-disable react/no-unstable-nested-components */

import {
	type ColumnDef,
	type ColumnFiltersState,
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	type SortingState,
	useReactTable,
	type VisibilityState,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import Badge from '@/components/Badge/Badge';
import { DataTableColumnHeader } from '@/components/DataTable/DataTableColumnHeader';
import { DataTablePagination } from '@/components/DataTable/DataTablePagination';
import TableFilters from '@/components/TableFilters/TableFilters';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { SetStatus } from '@/Enums/enum';
import { useCoursesAll } from '@/hooks/queries/useCoursesAll';
import { useHashScrollTo } from '@/hooks/useHashScrollTo';
import { usePermissions } from '@/hooks/usePermissions';
import { EditIcon, ViewIcon } from '@/icons';
import type { CourseWithRelations } from '@/lib/types';

const Courses = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	// scroll to course if hash is set in url
	useHashScrollTo();
	const { canEditCourse } = usePermissions();

	const { data: courses, isLoading } = useCoursesAll();

	const isArchived = JSON.parse(`${searchParams.get('showArchived') ?? false}`);

	type CourseTableData = {
		courseColumn: string;
		courseArchivedColumn: boolean;
		courseDivision: string | null | undefined;
		courseDiscipline: string | null | undefined;
		courseTeacher: CourseWithRelations;
		actionsColumn: CourseWithRelations;
		idColumn: string;
	};

	const queryCourseData = useMemo(() => {
		const filtered: CourseTableData[] =
			(courses
				?.filter((course) => course?.is_archived === isArchived)
				?.map((course) => ({
					courseColumn: course.course_name,
					courseArchivedColumn: Boolean(course.is_archived),
					courseDivision: course?.courses_division?.division?.name ?? null,
					courseDiscipline: course?.courses_discipline?.discipline?.name ?? null,
					courseTeacher: course,
					actionsColumn: course,
					idColumn: course.course_id,
				})) as CourseTableData[]) ?? [];

		// Same ordering as handleSetData: user's courses first, then sort by name (so table order matches when using queryCourseData)
		const userSorted = filtered.reduce<CourseTableData[]>(
			(acc, element) =>
				canEditCourse((element.actionsColumn.courses_users as CourseUserEntity[]) || [])
					? [element, ...acc]
					: [...acc, element],
			[]
		);
		const lastIdx = userSorted.filter((row: CourseTableData) =>
			canEditCourse((row.actionsColumn.courses_users as CourseUserEntity[]) || [])
		).length;
		const sortedUser = [...userSorted.slice(0, lastIdx)].sort((a, b) =>
			a.courseColumn > b.courseColumn ? 1 : a.courseColumn < b.courseColumn ? -1 : 0
		);
		return [...sortedUser, ...userSorted.slice(lastIdx)];
	}, [courses, isArchived, canEditCourse]);

	const [filteredData, setFilteredData] = useState<CourseTableData[]>([]);
	const [sorting, setSorting] = useState<SortingState>([]);
	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
	const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

	useEffect(() => {
		if (queryCourseData) {
			setFilteredData(queryCourseData as CourseTableData[]);
		}
	}, [queryCourseData]);

	const handleSetData = useCallback(
		(queryTableData: any) => {
			const userSortedCourses = queryTableData.reduce(
				(acc: CourseTableData[], element: CourseTableData) => {
					const canEdit = canEditCourse(
						(element.actionsColumn.courses_users as CourseUserEntity[]) || []
					);
					if (canEdit) {
						return [element, ...acc];
					}
					return [...acc, element];
				},
				[]
			);
			// get last index of user assigned course
			const lastIdx = userSortedCourses.filter((row: CourseTableData) => {
				const canEdit = canEditCourse(
					(row.actionsColumn.courses_users as CourseUserEntity[]) || []
				);
				if (canEdit) {
					return row;
				}
				return false;
			}).length;

			// sort user assigned courses
			const allSortedCourses = userSortedCourses
				.slice(0, lastIdx)
				.sort((a: CourseTableData, b: CourseTableData) => {
					if (a.courseColumn > b.courseColumn) return 1;
					if (a.courseColumn < b.courseColumn) return -1;
					return 0;
				})
				.concat(userSortedCourses.slice(lastIdx));

			setFilteredData(allSortedCourses);
		},
		[canEditCourse]
	);

	const columns = useMemo<ColumnDef<CourseTableData>[]>(
		() => [
			{
				accessorKey: 'courseColumn',
				header: ({ column }) => <DataTableColumnHeader column={column} title="Course" />,
				cell: ({ row }) => <div>{row.getValue('courseColumn')}</div>,
			},
			{
				accessorKey: 'courseTeacher',
				header: ({ column }) => <DataTableColumnHeader column={column} title="Teacher" />,
				cell: ({ row }) => {
					const { courses_users: courseUsers } = row.original.actionsColumn;
					if (courseUsers && courseUsers.length !== 0) {
						return (
							<div className="flex flex-col gap-1">
								{courseUsers.map(
									(courseUser: NonNullable<CourseWithRelations['courses_users']>[number]) => {
										const { user } = courseUser;
										return (
											<div key={`teacher-row-${row.original.idColumn}-${courseUser.user_id}`}>
												{`${user?.user_first ?? ''} ${user?.user_last ?? ''}`}
											</div>
										);
									}
								)}
							</div>
						);
					}
					return <div />;
				},
				enableSorting: false,
			},
			{
				accessorKey: 'courseDivision',
				header: ({ column }) => <DataTableColumnHeader column={column} title="Division" />,
				cell: ({ row }) => <div>{row.getValue('courseDivision') || '-'}</div>,
			},
			{
				accessorKey: 'courseDiscipline',
				header: ({ column }) => <DataTableColumnHeader column={column} title="Discipline" />,
				cell: ({ row }) => <div>{row.getValue('courseDiscipline') || '-'}</div>,
			},
			{
				id: 'actions',
				enableHiding: false,
				cell: ({ row }) => {
					const { actionsColumn } = row.original;
					const { is_saved_draft: hasDraft } = actionsColumn;

					const isPendingApproval = () => {
						if (actionsColumn?.submission) {
							return Object.values(actionsColumn.submission).every(
								(status) => status !== SetStatus.APPROVED
							);
						}
						return false;
					};

					const renderBadge = () => {
						if (hasDraft) {
							return <Badge style={{ marginRight: 'var(--spacing-2)' }} text="Draft" />;
						}

						if (isPendingApproval()) {
							return <Badge style={{ marginRight: 'var(--spacing-2)' }} text="Approval Pending" />;
						}
						return null;
					};

					const canEdit = canEditCourse((actionsColumn.courses_users as CourseUserEntity[]) || []);

					if (canEdit) {
						return (
							<div className="flex items-center justify-end">
								{renderBadge()}
								<Link to={`${actionsColumn.course_id}`}>
									<EditIcon passedEvent={() => false} />
								</Link>
							</div>
						);
					}
					return (
						<div className="flex items-center justify-end">
							<Link to={`${actionsColumn.course_id}`}>
								<ViewIcon />
							</Link>
						</div>
					);
				},
			},
		],
		[canEditCourse]
	);

	// Use queryCourseData when showing full list so draft badges are correct; use filteredData only when user has filtered to a subset
	const tableData =
		filteredData.length > 0 && queryCourseData && filteredData.length < queryCourseData.length
			? filteredData
			: (queryCourseData ?? []);

	const table = useReactTable({
		data: tableData,
		columns,
		onSortingChange: setSorting,
		onColumnFiltersChange: setColumnFilters,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		onColumnVisibilityChange: setColumnVisibility,
		state: {
			sorting,
			columnFilters,
			columnVisibility,
		},
		initialState: {
			pagination: {
				pageSize: 10,
			},
		},
	});
	return (
		<>
			<div className="flex">
				<label
					htmlFor="showArchivedCheckbox"
					className="flex items-center gap-2 text-[12px] cursor-pointer select-none self-end py-1 px-2 rounded-[4px] ml-auto mr-5 mb-6 w-[135px] bg-primary/5"
				>
					<Checkbox
						id="showArchivedCheckbox"
						checked={isArchived}
						onCheckedChange={(checked) => {
							const isChecked = checked === true;
							const updatedSearchParams = new URLSearchParams(searchParams.toString());
							if (isChecked) {
								updatedSearchParams.set('showArchived', 'true');
								setSearchParams(updatedSearchParams.toString());

								const archivedCourses = queryCourseData?.filter(
									(course) => course.courseArchivedColumn
								);
								setFilteredData((archivedCourses || []) as CourseTableData[]);
								return;
							}
							updatedSearchParams.set('showArchived', 'false');
							setSearchParams(updatedSearchParams.toString());

							setFilteredData((queryCourseData || []) as CourseTableData[]);
						}}
					/>
					{isArchived ? 'Hide Archived' : 'Show Archived'}
				</label>
				{queryCourseData && (
					<TableFilters
						data={queryCourseData}
						handleSetData={handleSetData}
						searchColumn={['courseColumn', 'courseDiscipline', 'courseTeacher']}
						filteredColumn="courseDivision"
						resultQty={tableData?.length ?? 0}
					/>
				)}
			</div>
			{isLoading && (
				<div className="flex flex-col pb-5 h-full">
					<Skeleton className="w-full mb-3 h-24" />
					<Skeleton className="w-full mb-3 h-24" />
					<Skeleton className="w-full mb-3 h-24" />
					<Skeleton className="w-full mb-3 h-24" />
				</div>
			)}
			{!isLoading && tableData && (
				<div className="flex-1 flex flex-col min-h-0">
					<div
						className="flex-1 overflow-auto rounded-md"
						style={{ maxHeight: 'calc(100vh - 300px)' }}
					>
						<Table className="text-md typo-body-2">
							<TableHeader className="sticky top-0 bg-background z-10">
								{table.getHeaderGroups().map((headerGroup) => (
									<TableRow key={headerGroup.id}>
										{headerGroup.headers.map((header) => {
											return (
												<TableHead key={header.id}>
													{header.isPlaceholder
														? null
														: flexRender(header.column.columnDef.header, header.getContext())}
												</TableHead>
											);
										})}
									</TableRow>
								))}
							</TableHeader>
							<TableBody>
								{table.getRowModel().rows?.length ? (
									table.getRowModel().rows.map((row) => (
										<TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
											{row.getVisibleCells().map((cell) => (
												<TableCell key={cell.id}>
													{flexRender(cell.column.columnDef.cell, cell.getContext())}
												</TableCell>
											))}
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell colSpan={columns.length} className="h-24 text-center">
											No results.
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</div>
					<div className="mt-4 shrink-0 pb-4 border-t border-gray-200 pt-4">
						<DataTablePagination table={table} />
					</div>
				</div>
			)}
		</>
	);
};

export default Courses;
